import { useMemo, useState } from 'react'

import { F0Box, F0Text } from '@factorialco/f0-react'

import { withCompanySalaryRange } from 'modules/job_catalog/benchmark/lib/companySalaryRange'
import { computeMarketPosition } from 'modules/job_catalog/benchmark/lib/marketPosition'
import SalaryIndicator from 'modules/job_catalog/benchmark/components/SalaryIndicator'
import type {
  AnchorRect,
  BenchmarkMatchResult,
  MarketPosition,
} from 'modules/job_catalog/benchmark/types'

import i18n from 'modules/core/lib/i18n'
import type { SalaryByWorkplaceGroup } from 'modules/job_catalog/lib/salaryUtils'

interface SalariesCardProps {
  groupedSalaries: SalaryByWorkplaceGroup[]
  showBenchmarkTrigger: boolean
  onOpenBenchmark: (workplaceId: string | null, anchorRect: AnchorRect) => void
  activeBenchmarkWorkplaceId?: string | null
  benchmarkData: BenchmarkMatchResult | null
  benchmarkLoading: boolean
}

interface SalariesCardRowProps {
  salaryGroup: SalaryByWorkplaceGroup
  isLast: boolean
  showBenchmarkTrigger: boolean
  onOpenBenchmark: (workplaceId: string | null, anchorRect: AnchorRect) => void
  activeBenchmarkWorkplaceId?: string | null
  benchmarkPosition: MarketPosition
  benchmarkLoading: boolean
}

const BACKGROUND_DEFAULT_HOVER = 'var(--f1-color-background-hover, #F3F4F6)'
const SECONDARY_HOVER = 'var(--f1-color-secondary-hover, #0F2E571A)'

const SalariesCardRow = ({
  salaryGroup,
  isLast,
  showBenchmarkTrigger,
  onOpenBenchmark,
  activeBenchmarkWorkplaceId,
  benchmarkPosition,
  benchmarkLoading,
}: SalariesCardRowProps) => {
  const [isSalaryHovered, setIsSalaryHovered] = useState(false)

  const benchmarkWorkplaceId = salaryGroup.groupKey === 'default' ? null : salaryGroup.groupKey
  const isBenchmarkActive = activeBenchmarkWorkplaceId === benchmarkWorkplaceId

  const salaryHoverBackground = isBenchmarkActive
    ? SECONDARY_HOVER
    : isSalaryHovered
      ? BACKGROUND_DEFAULT_HOVER
      : 'transparent'

  const canOpenBenchmark = showBenchmarkTrigger

  return (
    <F0Box
      display="flex"
      flexDirection="row"
      alignItems="center"
      justifyContent="between"
      padding="md"
      borderBottom={isLast ? undefined : 'default'}
      borderColor={isLast ? undefined : 'secondary'}
    >
      <F0Text content={salaryGroup.workplaceLabel} variant="description" />

      {canOpenBenchmark ? (
        /* eslint-disable-next-line factorial/no-raw-html -- Native <button> required for accessibility; F0Box role="button" renders a <div> which fails a11y audits */
        <button
          type="button"
          aria-label={`${i18n.t('job_catalog.benchmark.open_details')}: ${salaryGroup.workplaceLabel} ${salaryGroup.formattedRange}`}
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 4,
            cursor: 'pointer',
            background: salaryHoverBackground,
            transition: 'background-color 120ms ease',
            borderRadius: 200,
            padding: '2px 6px',
            border: 'none',
            font: 'inherit',
          }}
          onMouseEnter={() => setIsSalaryHovered(true)}
          onMouseLeave={() => setIsSalaryHovered(false)}
          onClick={(event) =>
            onOpenBenchmark(benchmarkWorkplaceId, event.currentTarget.getBoundingClientRect())
          }
        >
          <F0Text content={salaryGroup.formattedRange} variant="label" />
          <SalaryIndicator size="sm" position={benchmarkPosition} loading={benchmarkLoading} />
        </button>
      ) : (
        <F0Box display="flex" flexDirection="row" alignItems="center" gap="xs">
          <F0Text content={salaryGroup.formattedRange} variant="label" />
        </F0Box>
      )}
    </F0Box>
  )
}

const SalariesCard = ({
  groupedSalaries,
  showBenchmarkTrigger,
  onOpenBenchmark,
  activeBenchmarkWorkplaceId,
  benchmarkData,
  benchmarkLoading,
}: SalariesCardProps) => {
  const positionsByGroupKey = useMemo(() => {
    const map = new Map<string, MarketPosition>()
    if (!benchmarkData) return map
    for (const group of groupedSalaries) {
      const position = computeMarketPosition(
        withCompanySalaryRange(benchmarkData, group.referenceSalary)
      )
      map.set(group.groupKey, position)
    }
    return map
  }, [benchmarkData, groupedSalaries])

  return (
    <F0Box border="default" borderColor="secondary" borderRadius="md" overflow="hidden">
      {groupedSalaries.map((salaryGroup, index) => {
        return (
          <SalariesCardRow
            key={salaryGroup.groupKey}
            salaryGroup={salaryGroup}
            isLast={index === groupedSalaries.length - 1}
            showBenchmarkTrigger={showBenchmarkTrigger}
            onOpenBenchmark={onOpenBenchmark}
            activeBenchmarkWorkplaceId={activeBenchmarkWorkplaceId}
            benchmarkPosition={positionsByGroupKey.get(salaryGroup.groupKey) ?? 'unknown'}
            benchmarkLoading={benchmarkLoading}
          />
        )
      })}
    </F0Box>
  )
}

export default SalariesCard
